import * as React from "react"
import {
  CategoryType,
  ClassificationSource,
  DirectionFromOrigin,
  useSummarizeTransactionsByCategoryQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined } from "@digits-shared/helpers/filters"
import numberHelper from "@digits-shared/helpers/numberHelper"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const HOURS_PER_TRANSACTION = 34 / 60 / 60 // seconds / 60 / 60
const ELIGIBLE_TOP_CATEGORY_TYPES = [CategoryType.Expenses, CategoryType.Income]

export interface ClassificationSummary {
  categoryId?: string
  name: string
  displayKey?: string
  count: number
  totalCount: number
}

interface ClassificationSummaryResult {
  summaries: ClassificationSummary[]
  totalTransactions: number
  hoursSaved: number
  loading: boolean
}

export function useCategoryClassificationSummaries(
  selectedSource: ClassificationSource = ClassificationSource.Digits,
  skip?: boolean
): ClassificationSummaryResult {
  const viewKey = useViewVersion()
  const origin = useIntervalOrigin()

  const variables = React.useMemo(
    () => ({
      origin: {
        ...origin,
        intervalCount: origin.intervalCount || 1,
      },
      direction: DirectionFromOrigin.Past,
      filter: {
        viewKey,
        withoutCategoryAncestors: true,
        categoryTypes: Array.from(Object.values(CategoryType)),
      },
    }),
    [origin, viewKey]
  )

  const { data, loading } = useSummarizeTransactionsByCategoryQuery({
    variables: {
      ...variables,
      filter: {
        ...variables.filter,
        categoryClassificationSources: [selectedSource],
      },
    },
    skip,
  })
  const { data: totalData, loading: totalLoading } = useSummarizeTransactionsByCategoryQuery({
    variables,
    skip,
  })

  const summaries = React.useMemo(() => {
    const sourceSummaries = data?.dimensionalTransactionSummary?.category || []
    const totalSummaries = totalData?.dimensionalTransactionSummary?.category || []

    const totalCountByCategory = totalSummaries.reduce((acc, { categoryObject, summary }) => {
      acc.set(categoryObject.id, summary?.total?.transactionsCount || 0)
      return acc
    }, new Map<string, number>())

    const topSummaries: ClassificationSummary[] = sourceSummaries
      .filter((s) => ELIGIBLE_TOP_CATEGORY_TYPES.includes(s.categoryObject.type))
      .sort((a, b) => b.summary.total.transactionsCount - a.summary.total.transactionsCount)
      .slice(0, 4)
      .map((item) => {
        const categoryId = item.categoryObject.id
        return {
          categoryId,
          name: item.categoryObject.name,
          count: item.summary.total.transactionsCount,
          totalCount: totalCountByCategory.get(categoryId) || 0,
          displayKey: item.categoryObject.displayKey,
        }
      })

    const topCategoryIDs = new Set<string>(topSummaries.map((s) => s.categoryId).filter(defined))
    const otherSummary: ClassificationSummary = sourceSummaries
      .filter((s) => !topCategoryIDs.has(s.categoryObject.id))
      .reduce(
        (acc, item) => {
          const count = item.summary.total.transactionsCount
          const totalCount = totalCountByCategory.get(item.categoryObject.id) || 0
          return {
            ...acc,
            count: acc.count + count,
            totalCount: acc.totalCount + totalCount,
          }
        },
        {
          name: topCategoryIDs.size === 0 ? "View Categories" : "Other Categories",
          displayKey: "Tags",
          count: 0,
          totalCount: 0,
        }
      )

    return topSummaries.concat(otherSummary.count > 0 ? [otherSummary] : [])
  }, [
    data?.dimensionalTransactionSummary?.category,
    totalData?.dimensionalTransactionSummary?.category,
  ])

  const totalTransactions = React.useMemo(
    () => summaries.reduce((sum, item) => sum + item.count, 0),
    [summaries]
  )

  return {
    summaries,
    totalTransactions,
    hoursSaved: Math.round(totalTransactions * HOURS_PER_TRANSACTION),
    loading: loading || totalLoading,
  }
}

export function formatAgentTransactions(count: number) {
  return numberHelper
    .numberFormatter({
      useGrouping: true,
      notation: "compact",
    })
    .format(count)
}
